// apps/frontend-vario-kaufland/src/views/shared/combobox.ts
//
// Eingabefeld mit Vorschlagsliste fuer Felder mit vordefinierten Kaufland-Werten.
//
// Warum kein einfaches <select>: `manufacturer` ist ein Shared Set ueber Kauflands
// gesamte Markenliste, und die Liste kann unvollstaendig sein. Der Haendler muss deshalb
// auch tippen duerfen, was eine <datalist> erlaubt und ein <select> nicht.
//
// Die Begrenzung der Liste passiert eine Stufe frueher: `kaufland-listing-schema` liefert
// hoechstens 500 Werte je Attribut und meldet ein Abschneiden ueber
// `allowedValuesTruncated`. Was hier ankommt, geht deshalb VOLLSTAENDIG ins DOM.

export interface ComboboxWert {
  value: string;
  label: string;
}

/** Default-Obergrenze fuer `filtereWerte`. Betrifft nur die Suchfunktion, NICHT die
 *  Datalist, siehe Kommentar an `renderCombobox`. */
const MAX_TREFFER = 50;

export function filtereWerte(
  werte: ComboboxWert[],
  suche: string,
  limit: number = MAX_TREFFER,
): ComboboxWert[] {
  const s = suche.trim().toLowerCase();
  if (s === '') return werte.slice(0, limit);
  const treffer: ComboboxWert[] = [];
  for (const w of werte) {
    if (w.label.toLowerCase().includes(s) || w.value.toLowerCase().includes(s)) {
      treffer.push(w);
      if (treffer.length >= limit) break;
    }
  }
  return treffer;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Rendert ein Textfeld mit angehaengter datalist. Der Haendler kann tippen oder
 * auswaehlen; getippte Werte bleiben erlaubt, weil Kauflands Liste nicht immer
 * vollstaendig ist.
 *
 * Fix-Runde 1, Befund 2: Die Datalist wird NICHT nach `aktuell` vorgefiltert. Eine
 * `<datalist>` filtert im Browser bereits selbst nach dem, was der Nutzer gerade tippt --
 * eine serverseitige Vorfilterung nach dem gespeicherten Wert war deshalb nicht nur
 * ueberfluessig, sondern zeigte bei einem bereits gesetzten Wert (z.B. 'NEW' unter neun
 * Zustandscodes) nur noch die Teiltreffer davon an und versteckte alle anderen gueltigen
 * Werte. Es gibt hier bewusst keinen Event-Listener, der bei jedem Tastendruck neu rendert:
 * die `<datalist>` erledigt das Filtern selbst. `filtereWerte` bleibt exportiert und
 * getestet fuer die eigentliche Such-/Filterlogik (Tests + kuenftige Verwendung).
 *
 * Abschlussreview 2026-08-09, Befund W2: Auch eine Kuerzung auf 50 Optionen gibt es nicht
 * mehr. Eine `<datalist>` kann nur ueber das filtern, was im DOM steht -- bei einem Attribut
 * mit 300 Werten waren 250 davon unerreichbar, und `allowedValuesTruncated` schlug nicht an,
 * weil es erst oberhalb von 500 greift. Der Haendler tippte einen gueltigen Wert, bekam
 * keinen Vorschlag und hielt ihn fuer nicht vorhanden. 500 `<option>`-Elemente sind fuer den
 * Browser unkritisch, die Obergrenze setzt die Edge Function.
 */
export function renderCombobox(args: {
  fieldKey: string;
  werte: ComboboxWert[];
  aktuell: string;
  platzhalter?: string;
  /** Welches Daten-Attribut den Wert traegt: `data-field-value` (Kategorie-Festwert, Default)
   *  oder `data-field-override` (Artikelwert). Die Change-Handler des Editors haengen an
   *  genau diesen Attributen. */
  datenAttribut?: 'data-field-value' | 'data-field-override';
  /** CSS-Klasse des Eingabefelds, etwa `kl-map-field-override-input is-set`. */
  klasse?: string;
  /** Zusatz fuer die Datalist-ID, wenn dasselbe Feld zwei Comboboxen in einer Zeile hat
   *  (Kategorie-Festwert UND Artikelwert) -- sonst teilen sie sich eine ID und der Browser
   *  zeigt beiden dieselbe Liste. */
  listenSuffix?: string;
}): string {
  const suffix = args.listenSuffix ? `-${args.listenSuffix}` : '';
  const listenId = `kl-cb-${args.fieldKey.replace(/[^a-zA-Z0-9_-]/g, '-')}${suffix}`;
  const attribut = args.datenAttribut ?? 'data-field-value';
  const klasse = args.klasse ? ` class="${escapeHtml(args.klasse)}"` : '';
  const optionen = args.werte
    .map((w) => `<option value="${escapeHtml(w.value)}">${escapeHtml(w.label)}</option>`)
    .join('');
  return `
    <input type="text" list="${listenId}"${klasse} ${attribut}="${escapeHtml(args.fieldKey)}"
           value="${escapeHtml(args.aktuell)}"
           placeholder="${escapeHtml(args.platzhalter ?? 'Wert wählen oder eingeben …')}" />
    <datalist id="${listenId}">${optionen}</datalist>`;
}
